import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import {
  LICENSE_SCAN_FIELD_KEYS,
  type LicenseScanCandidateResponse,
  type LicenseScanFieldKey,
  type PatientResponse,
} from '@emr/shared';

import { listLicenseScans, resolveLicenseScan } from '../../api/licenseScan.ts';
import { getPatient } from '../../api/patients.ts';
import { errorMessage } from '../../api/client.ts';
import { GatedRoute } from '../../components/GatedRoute.tsx';
import { Badge, Button, ErrorText, Panel } from '../../components/primitives.tsx';
import { QuerySection } from '../../components/QuerySection.tsx';
import { formatCalendarDate } from '../../lib/dateFormat.ts';

/**
 * The staff-facing driver's-license scan review queue for one patient (`BACKLOG.md` C19,
 * `docs/decisions/patient-license-scan-scope.md` §2) — reached from `PatientChartPage.tsx`'s own
 * `LicenseScanTokenPanel`, in the same register `PatientDuplicatesPage.tsx` occupies for its own
 * review queue: a field-by-field accept/keep decision against a known, bounded candidate set, never
 * a general free-text edit form (this codebase's first staff-facing demographics-*write* surface, per
 * the decision doc's own survey — deliberately narrower than one for exactly that reason).
 *
 * **Gated on `patient:read`/`patient:write`**, matching `licenseScanReviewRoutes.ts`'s own capability
 * choice — this is demographic PHI about one specific patient, not the administrative-metadata
 * register `PatientDuplicatesPage.tsx` occupies, so a confidentiality-label denial
 * (`chart_access_denied`) is a real, expected response here, surfaced via `QueryFailureNotice`-style
 * handling rather than assumed away.
 */
export function PatientLicenseScanReviewPage() {
  const { t } = useTranslation('licenseScan');
  const { patientId } = useParams<{ patientId: string }>();
  if (patientId === undefined) {
    // Unreachable given `App.tsx`'s own `patients/:patientId/license-scans` route.
    return null;
  }

  return (
    <GatedRoute title={t('reviewPageTitle')} capability="patient:read" feature={null}>
      <LicenseScanReviewQueue patientId={patientId} />
    </GatedRoute>
  );
}

function LicenseScanReviewQueue({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('licenseScan');
  const queryClient = useQueryClient();
  const queryKey = ['patients', patientId, 'license-scans'];

  const patientQuery = useQuery({
    queryKey: ['patient', patientId],
    queryFn: ({ signal }) => getPatient(patientId, signal),
  });
  const scansQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => listLicenseScans(patientId, signal).then((r) => r.scans),
  });

  const resolve = useMutation({
    mutationFn: (input: {
      scanId: string;
      status: 'confirmed' | 'rejected';
      acceptedFields: string[];
    }) =>
      resolveLicenseScan(
        patientId,
        input.scanId,
        input.status === 'confirmed'
          ? { status: 'confirmed', acceptedFields: input.acceptedFields as LicenseScanFieldKey[] }
          : { status: 'rejected' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-ink-muted">
        <Link
          to={`/patients/${patientId}`}
          className="font-medium text-accent-text underline-offset-2 hover:underline"
        >
          {t('backToChartLink')}
        </Link>
      </p>
      <p className="m-0 text-sm text-ink-muted">{t('description')}</p>
      <Panel>
        <QuerySection query={patientQuery} loadingLabel={t('loading')}>
          {(patient) => (
            <QuerySection
              query={scansQuery}
              loadingLabel={t('loading')}
              isEmpty={(scans) => scans.length === 0}
              emptyMessage={t('emptyMessage')}
            >
              {(scans) => (
                <ul className="m-0 flex list-none flex-col gap-4 p-0">
                  {scans.map((scan) => (
                    <ScanCard
                      key={scan.id}
                      scan={scan}
                      patient={patient}
                      pending={resolve.isPending && resolve.variables.scanId === scan.id}
                      onResolve={(status, acceptedFields) => {
                        resolve.mutate({ scanId: scan.id, status, acceptedFields });
                      }}
                    />
                  ))}
                </ul>
              )}
            </QuerySection>
          )}
        </QuerySection>
        {resolve.isError ? <ErrorText message={errorMessage(resolve.error)} /> : null}
      </Panel>
    </div>
  );
}

/** The patient-field name each `LicenseScanFieldKey` corresponds to, for reading the current value
 * off `PatientResponse`. */
function currentValueFor(patient: PatientResponse, key: LicenseScanFieldKey): string | null {
  return patient[key] ?? null;
}

function ScanCard({
  scan,
  patient,
  pending,
  onResolve,
}: {
  readonly scan: LicenseScanCandidateResponse;
  readonly patient: PatientResponse;
  readonly pending: boolean;
  readonly onResolve: (status: 'confirmed' | 'rejected', acceptedFields: string[]) => void;
}) {
  const { t } = useTranslation('licenseScan');

  const decidableKeys = useMemo(
    () =>
      LICENSE_SCAN_FIELD_KEYS.filter((key) => {
        const scanned = scan.extractedFields[key];
        return scanned !== undefined && scanned !== currentValueFor(patient, key);
      }),
    [scan, patient],
  );

  // Pre-checked exactly where the current value is blank — the decision doc's own "the clearest,
  // lowest-risk case to pre-fill." Overwriting an existing, different value needs an explicit,
  // unchecked-by-default opt-in from the reviewer.
  const [accepted, setAccepted] = useState<ReadonlySet<LicenseScanFieldKey>>(
    () => new Set(decidableKeys.filter((key) => currentValueFor(patient, key) === null)),
  );

  if (scan.status !== 'pending') {
    return (
      <li className="flex flex-col gap-2 rounded-panel border border-row-line bg-surface p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={scan.status === 'confirmed' ? 'success' : 'neutral'}>
            {t(`status.${scan.status}`)}
          </Badge>
          <span className="text-sm text-ink-muted">
            {scan.resolvedAt !== null
              ? t('resolvedOnLabel', { date: formatCalendarDate(scan.resolvedAt.slice(0, 10)) })
              : null}
          </span>
        </div>
        {scan.confirmedFields !== null && scan.confirmedFields.length > 0 ? (
          <p className="m-0 text-sm text-ink-muted">
            {t('appliedFieldsLabel', {
              fields: scan.confirmedFields.map((key) => t(`fieldLabel.${key}`)).join(', '),
            })}
          </p>
        ) : null}
      </li>
    );
  }

  return (
    <li className="flex flex-col gap-3 rounded-panel border border-row-line bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="accent">{t('pendingBadge')}</Badge>
        <span className="text-sm text-ink-muted">
          {t('capturedOnLabel', { date: formatCalendarDate(scan.createdAt.slice(0, 10)) })}
        </span>
      </div>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-row-line text-left text-ink-muted">
            <th className="py-1 pr-2 font-medium">{t('fieldColumnHeader')}</th>
            <th className="py-1 pr-2 font-medium">{t('currentValueColumnHeader')}</th>
            <th className="py-1 pr-2 font-medium">{t('scannedValueColumnHeader')}</th>
            <th className="py-1 font-medium">{t('useScannedColumnHeader')}</th>
          </tr>
        </thead>
        <tbody>
          {LICENSE_SCAN_FIELD_KEYS.filter((key) => scan.extractedFields[key] !== undefined).map(
            (key) => {
              const scanned = scan.extractedFields[key] ?? '';
              const current = currentValueFor(patient, key);
              const matches = current === scanned;
              return (
                <tr key={key} className="border-b border-row-line last:border-0">
                  <td className="py-2 pr-2 font-medium text-ink">{t(`fieldLabel.${key}`)}</td>
                  <td className="max-w-[16ch] py-2 pr-2 wrap-anywhere text-ink-muted">
                    {current ?? <em>{t('blankValue')}</em>}
                  </td>
                  <td className="max-w-[16ch] py-2 pr-2 wrap-anywhere text-ink">{scanned}</td>
                  <td className="py-2">
                    {matches ? (
                      <span className="text-xs text-ink-muted">{t('matchesCurrentValue')}</span>
                    ) : (
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={accepted.has(key)}
                          onChange={(event) => {
                            setAccepted((previous) => {
                              const next = new Set(previous);
                              if (event.target.checked) next.add(key);
                              else next.delete(key);
                              return next;
                            });
                          }}
                        />
                        <span className="sr-only">{t('useScannedColumnHeader')}</span>
                      </label>
                    )}
                  </td>
                </tr>
              );
            },
          )}
        </tbody>
      </table>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          pending={pending}
          onClick={() => {
            onResolve('confirmed', [...accepted]);
          }}
        >
          {t('actions.confirmApply')}
        </Button>
        <Button
          variant="secondary"
          pending={pending}
          onClick={() => {
            onResolve('rejected', []);
          }}
        >
          {t('actions.reject')}
        </Button>
      </div>
    </li>
  );
}
